import { createNavigation } from '../components/navigation.js';
import { createFooter } from '../components/footer.js';

export function createTermsPrivacyPage(): string {
  return `
    ${createNavigation()}

    <!-- Legal Content -->
    <section class="legal-page">
        <div class="legal-container">
            <!-- Header -->
            <div class="legal-header">
                <h1 class="legal-main-title">Legal Information</h1>
                <p class="legal-subtitle">
                    Your privacy and trust are important to us. Here you'll find our Terms of Service and Privacy Policy.
                </p>
            </div>

            <!-- Quick Summary -->
            <div class="legal-summary">
                <h2>Quick Summary (read this first)</h2>
                <ul>
                    <li>Firebase SDKs collect more than analytics events: Crashlytics grabs stack traces + device metadata, Performance Monitoring logs network-latency and <strong>IP addresses</strong>, Messaging stores the <strong>APNs token</strong>, etc.</li>
                    <li>Apple requires you to surface every one of those data types in two places: the in-app Privacy Policy URL and the <strong>App Privacy "nutrition label."</strong></li>
                    <li>None of the Firebase libraries use that data for <strong>cross-app tracking</strong> unless you also integrate Google Ads / AdMob; VibeChk does <strong>not</strong>, so your ATT posture ("we don't track") is unchanged—but you must still show the ATT prompt if you ever add ad IDs later.</li>
                    <li>The policy below now:
                        <ul>
                            <li>Adds <strong>Device & Diagnostics</strong> rows to the data‑inventory table (to cover IP address, crash traces, performance metrics).</li>
                            <li>Explains Firebase's "always collected," "default," and "usage‑dependent" buckets.</li>
                            <li>Clarifies that Google LLC is your main sub‑processor under a DPA that incorporates Standard Contractual Clauses.</li>
                            <li>Points readers to Apple's privacy controls ("Limit Ad Tracking," ATT settings).</li>
                        </ul>
                    </li>
                </ul>
            </div>

            <!-- Content -->
            <div class="legal-content">
                <!-- Privacy Policy -->
                <div class="legal-section" id="privacy">
                    <div class="legal-section-header">
                        <h2 class="legal-section-title">Privacy Policy</h2>
                        <div class="legal-divider"></div>
                        <p class="legal-last-updated">Last updated: 6 July 2025</p>
                    </div>

                    <div class="legal-text">
                        <div class="legal-item">
                            <h3>1. Who we are</h3>
                            <p>VibeChk, App. ("VibeChk," "we," "our," or "us") operates the VibeChk mobile application and the website vibechk.io.</p>
                            <p>For privacy questions, please email justin@vibechk.io.</a></p>
                        </div>

                        <div class="legal-item">
                            <h3>2. Scope</h3>
                            <p>This Policy explains how we collect, use, disclose, and protect personal data when you use our app, site, or related services.</p>
                        </div>

                        <div class="legal-item">
                            <h3>3. What we collect & why (expanded)</h3>
                            <div class="data-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Data category</th>
                                            <th>Examples</th>
                                            <th>Purpose(s)</th>
                                            <th>Linked to user?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Contact Info</strong></td>
                                            <td>Name, email</td>
                                            <td>Account, profile, support, product updates</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Location</strong></td>
                                            <td>GPS coords (on‑demand)</td>
                                            <td>Show nearby creators, map analytics</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Identifiers</strong></td>
                                            <td>User ID, device‑push token</td>
                                            <td>Auth, invites, fraud prevention</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td><strong>User Content</strong></td>
                                            <td>Avatar, one‑line pitch, creator type, availability, meetup address</td>
                                            <td>Profile sharing, invite scheduling, map analytics</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Device & Diagnostics</strong></td>
                                            <td><strong>IP address, device & OS info, crash stack traces, performance metrics</strong></td>
                                            <td>Crash analytics (Firebase Crashlytics), performance monitoring, SDK QA</td>
                                            <td>No direct identity link</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Usage Data</strong></td>
                                            <td>Interaction events, feature toggles</td>
                                            <td>Feature planning, stability</td>
                                            <td>No</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p><em>Sources: Firebase Crashlytics data map, Performance Monitoring IP usage, Google Analytics event logging; Apple nutrition‑label schema.</em></p>
                        </div>

                        <div class="legal-item">
                            <h3>4. How we use your data (clarifies Firebase)</h3>
                            <ul class="legal-list">
                                <li><strong>App functionality</strong> – authentication, profile display, invite delivery, map refresh.</li>
                                <li><strong>Diagnostics & safety</strong> – <strong>Firebase Crashlytics</strong> captures crash reports + device state to debug issues; <strong>Performance Monitoring</strong> logs latency and IP for geography segmentation.</li>
                                <li><strong>Analytics</strong> – <strong>Firebase Analytics</strong> processes aggregated events; we have disabled "IDFA collection," so no cross‑app tracking.</li>
                            </ul>
                        </div>

                        <div class="legal-item">
                            <h3>5. Legal bases (GDPR)</h3>
                            <p>We process personal data only when we have a lawful basis: (i) performance of a contract (providing the service), (ii) legitimate interests (security, analytics), (iii) consent (marketing emails, location access), or (iv) legal obligation.</p>
                        </div>

                        <div class="legal-item">
                            <h3>6. Sharing & service providers (expanded)</h3>
                            <div class="data-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Vendor</th>
                                            <th>Service</th>
                                            <th>Data they receive</th>
                                            <th>Safeguard</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Google LLC (Firebase)</strong></td>
                                            <td>Auth, Analytics, Crashlytics, Messaging, Performance</td>
                                            <td>identifiers, diagnostics, IP, usage logs</td>
                                            <td>DPA + SCCs 2023‑10‑05</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p>We do <strong>not</strong> use AdMob or Google Ads, so Firebase data is <strong>not</strong> combined with advertising IDs.</p>
                        </div>

                        <div class="legal-item">
                            <h3>7. User rights</h3>
                            <div class="data-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Jurisdiction</th>
                                            <th>Rights</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>GDPR (EEA/UK)</strong></td>
                                            <td>access, rectification, erasure, portability, objection, restriction, lodge complaint with a supervisory authority.</td>
                                        </tr>
                                        <tr>
                                            <td><strong>CCPA (California)</strong></td>
                                            <td>know, delete, opt out of sale (we do not sell), non‑discrimination.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p>Please email justin@vibechk.io</a> to exercise any of these rights.</p>
                        </div>

                        <div class="legal-item">
                            <h3>8. Your choices</h3>
                            <ul class="legal-list">
                                <li>Location permissions – request foreground GPS access only when you tap "Update my location"; disable anytime in device settings.</li>
                                <li>Push notifications – managed via iOS Settings.</li>
                            </ul>
                        </div>

                        <div class="legal-item">
                            <h3>9. Security</h3>
                            <p>We encrypt data in transit (TLS 1.2+) and at rest; Firebase restricts employee access and logs every access event.</p>
                        </div>

                        <div class="legal-item">
                            <h3>10. Data retention</h3>
                            <p>Profile data persists until you delete your account or remain inactive for 24 months, whichever comes first. Crash & analytics logs stored no longer than 26 months.</p>
                        </div>

                        <div class="legal-item">
                            <h3>11. Children</h3>
                            <p>VibeChk is <strong>not</strong> directed to children under 16; accounts found to belong to minors will be deleted.</p>
                        </div>

                        <div class="legal-item">
                            <h3>12. International transfers</h3>
                            <p>If you access the service from outside the U.S., your data may be processed in the U.S. with Standard Contractual Clauses.</p>
                        </div>

                        <div class="legal-item">
                            <h3>16. Changes to this Policy</h3>
                            <p>We will post any changes here and update the "Last updated" date. Material changes announced in‑app or via email at least 14 days before effect.</p>
                        </div>

                        <div class="legal-item">
                            <h3>14. Firebase SDK appendix (new)</h3>
                            <div class="data-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>SDK</th>
                                            <th>Always collected</th>
                                            <th>"Default" collection</th>
                                            <th>Usage‑dependent collection</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Crashlytics</td>
                                            <td>crash traces, device/OS info</td>
                                            <td>—</td>
                                            <td>custom keys & logs</td>
                                        </tr>
                                        <tr>
                                            <td>Performance</td>
                                            <td>IP, latency, CPU/Memory</td>
                                            <td>—</td>
                                            <td>custom traces</td>
                                        </tr>
                                        <tr>
                                            <td>Messaging</td>
                                            <td>APNs token, device model/lang/OS</td>
                                            <td>Firebase user agent</td>
                                            <td>GA notification events (disabled)</td>
                                        </tr>
                                        <tr>
                                            <td>Authentication</td>
                                            <td>internal auth IDs</td>
                                            <td>Firebase user agent</td>
                                            <td>display name, email, phone (if provided)</td>
                                        </tr>
                                        <tr>
                                            <td>Analytics</td>
                                            <td>event metadata (no IDFA)</td>
                                            <td>—</td>
                                            <td>none</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Terms of Service -->
                <div class="legal-section" id="terms">
                    <div class="legal-section-header">
                        <h2 class="legal-section-title">Terms of Service</h2>
                        <div class="legal-divider"></div>
                        <p class="legal-last-updated">Last updated: 6 July 2025</p>
                    </div>

                    <div class="legal-text">
                        <div class="legal-item">
                            <h3>1. Acceptance</h3>
                            <p>By creating an account or using VibeChk, you agree to these Terms and to our Privacy Policy.</p>
                        </div>

                        <div class="legal-item">
                            <h3>2. Eligibility</h3>
                            <p>You must be at least 16 years old (or the minimum age of digital consent in your country) and capable of forming a binding contract. Apps that collect data from children must comply with COPPA; we do not target children.</p>
                        </div>

                        <div class="legal-item">
                            <h3>3. Account Registration</h3>
                            <p>Provide accurate information and keep it current. You are responsible for securing your device and passwords.</p>
                        </div>

                        <div class="legal-item">
                            <h3>4. License & Intellectual Property</h3>
                            <p>We grant you a limited, revocable, non‑exclusive license to use the app for personal, non‑commercial purposes. All trademarks, logos, and software remain our property.</p>
                        </div>

                        <div class="legal-item">
                            <h3>5. User Content</h3>
                            <p>You retain ownership of content you upload but grant VibeChk a worldwide, royalty‑free license to host, display, and distribute it solely to operate and improve the service. You represent that you have all rights to the content you post.</p>
                        </div>

                        <div class="legal-item">
                            <h3>6. Prohibited Conduct</h3>
                            <p>You agree not to:</p>
                            <ul class="legal-list">
                                <li>(i) impersonate others;</li>
                                <li>(ii) harvest personal data;</li>
                                <li>(iii) post unlawful, harassing, or infringing content;</li>
                                <li>(iv) reverse‑engineer or interfere with the service;</li>
                                <li><strong>(v) attempt to derive Firebase's source code or misrepresent our data‑collection disclosures.</strong></li>
                            </ul>
                        </div>

                        <div class="legal-item">
                            <h3>7. In‑Person Safety Release</h3>
                            <p>Meeting people you discover through VibeChk is at your own risk. Always choose public venues and take reasonable precautions. VibeChk disclaims liability for offline interactions to the fullest extent allowed by law.</p>
                        </div>

                        <div class="legal-item">
                            <h3>8. Third‑Party Services & Links</h3>
                            <p>Profiles may contain links to external sites; we are not responsible for those sites or their content.</p>
                        </div>

                        <div class="legal-item">
                            <h3>9. Termination</h3>
                            <p>We may suspend or terminate your account at any time for violation of these Terms or illegal conduct. You may delete your account in‑app.</p>
                        </div>

                        <div class="legal-item">
                            <h3>10. Disclaimers</h3>
                            <p>The service is provided "as is" without warranties of any kind. <strong>We rely on third‑party infrastructure (e.g., Firebase); while we monitor their uptime, we cannot guarantee uninterrupted availability.</strong></p>
                        </div>

                        <div class="legal-item">
                            <h3>11. Limitation of Liability</h3>
                            <p>To the maximum extent permitted by law, VibeChk shall not be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising out of your use of the app.</p>
                        </div>

                        <div class="legal-item">
                            <h3>12. Indemnification</h3>
                            <p>You agree to indemnify and hold harmless VibeChk and its officers, directors, and employees from any claims or damages arising from your use of the service or violation of these Terms.</p>
                        </div>

                        <div class="legal-item">
                            <h3>16. Governing Law & Dispute Resolution</h3>
                            <p>These Terms are governed by the laws of Washington State, USA. Any dispute will be resolved by <strong>binding arbitration in Seattle</strong> under the AAA Consumer Arbitration Rules. You waive any right to participate in a class action.</p>
                        </div>

                        <div class="legal-item">
                            <h3>14. Changes to Terms</h3>
                            <p>We may update these Terms; continued use after changes take effect constitutes acceptance. Material changes announced in‑app or via email at least 14 days before effect.</p>
                        </div>

                        <div class="legal-item">
                            <h3>15. Contact</h3> <!-- link to https://vibechk.io/contact -->
                            <p>Questions? Please email justin@vibechk.io</a> or write a message on our <a href="https://vibechk.io/contact">contact page</a>.</p>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Bottom Navigation -->
            <div class="legal-bottom-nav">
                <a href="/" class="back-home">← Back to Home</a>
                <div class="legal-nav-links">
                    <a href="#privacy" class="quick-link">Privacy</a>
                    <a href="#terms" class="quick-link">Terms</a>
                    <a href="/contact" class="quick-link">Contact</a>
                </div>
            </div>
        </div>
    </section>

    ${createFooter()}
  `;
} 